// Narrow-viewport detection for defaulting the transaction surfaces to the
// compact (mobile-style) interface. Used ONLY to seed the INITIAL view state —
// never to force a reactive switch mid-session, so a user who switches back to
// the desktop list on a small screen stays there.

import { useEffect, useState } from 'react'

// Default breakpoint below which a phone-width layout is assumed.
export const MOBILE_BREAKPOINT_PX = 768

// SSR-safe one-shot check: is the current viewport narrower than the
// breakpoint? Returns false when there is no window (server render / tests).
export function isMobileViewport(breakpointPx: number = MOBILE_BREAKPOINT_PX): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia(`(max-width: ${breakpointPx}px)`).matches
}

// Reactive variant: tracks the breakpoint across viewport changes. Hosts that
// want live re-layout can use this; the default-view seeders use the one-shot
// `isMobileViewport` instead so switching is never yanked away from the user.
export function useIsMobile(breakpointPx: number = MOBILE_BREAKPOINT_PX): boolean {
  const [mobile, setMobile] = useState(() => isMobileViewport(breakpointPx))
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    const mql = window.matchMedia(`(max-width: ${breakpointPx}px)`)
    const onChange = () => setMobile(mql.matches)
    onChange()
    // addEventListener('change') is the modern API; older Safari needs
    // addListener. Support both.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    mql.addListener(onChange)
    return () => mql.removeListener(onChange)
  }, [breakpointPx])
  return mobile
}
